import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { interviewModeLabel, interviewStatusLabel } from "@/lib/constants";
import { CalendarCheck, CheckCircle2, XCircle } from "lucide-react";

export default function EmployerInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/interviews/mine");
      setInterviews(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (interviewId, status) => {
    setError("");
    try {
      await api.patch(`/interviews/${interviewId}`, { status });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div data-testid="employer-interviews" className="bg-white rounded-xl border border-slate-200">
      <div className="p-5 border-b border-slate-100 flex items-center gap-2">
        <CalendarCheck className="w-5 h-5 text-brand-blue" />
        <h2 className="font-semibold text-brand-navy">Entretiens ({interviews.length})</h2>
      </div>
      {error && (
        <div data-testid="employer-interviews-error" className="m-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>
      )}
      <div className="divide-y divide-slate-50">
        {interviews.map((i) => (
          <div key={i.interview_id} data-testid={`employer-interview-${i.interview_id}`} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-brand-navy text-sm">{i.candidate_name} — {i.offer_title}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {i.scheduled_at ? new Date(i.scheduled_at).toLocaleString("fr-CA") : "—"} · {interviewModeLabel(i.mode)}
                {i.location_or_link ? ` · ${i.location_or_link}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                data-testid={`employer-interview-status-${i.interview_id}`}
                className={`text-xs font-medium rounded-full px-3 py-1 ${
                  i.status === "annule" ? "bg-red-50 text-red-600" : i.status === "realise" ? "bg-green-50 text-green-700" : "bg-brand-sky text-brand-navy"
                }`}
              >
                {interviewStatusLabel(i.status)}
                {i.candidate_response === "accepte" ? " · Accepté" : i.candidate_response === "decline" ? " · Décliné" : ""}
              </span>
              {i.status === "planifie" && (
                <>
                  <button
                    data-testid={`interview-done-${i.interview_id}`}
                    onClick={() => setStatus(i.interview_id, "realise")}
                    className="inline-flex items-center gap-1 text-xs font-medium text-green-700 border border-green-300 rounded-lg px-3 py-1.5 hover:bg-green-50"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Réalisé
                  </button>
                  <button
                    data-testid={`interview-cancel-${i.interview_id}`}
                    onClick={() => setStatus(i.interview_id, "annule")}
                    className="inline-flex items-center gap-1 text-xs font-medium text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Annuler
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {interviews.length === 0 && (
          <p data-testid="employer-interviews-empty" className="px-5 py-8 text-center text-sm text-slate-400">
            Aucun entretien planifié. Planifiez-en un depuis une candidature reçue.
          </p>
        )}
      </div>
    </div>
  );
}
